import {Component, Host, h, Prop, Event, type EventEmitter, Listen} from '@stencil/core';
import {ResultsSorting} from '../result-sorting/results-sorting';
import {sortingOptions} from '../../../../../constants/sortingConstants';
import type {SearchApi} from '../../../../../api/model/response/ApiResponse';
import {makeLogger} from '../../../../../logger/logger';
import type {FacetValue} from '../../../../../api/model/response/facets/Facets';
import {FilterNode} from '../../../facets-build-wrapper/filter-node';
import {GridSwitchButton} from '../grid-switch-button/grid-switch-button';
import type {FacetsShowChildrenType} from '../../../../strategies/types/facets-config-strategy-data.type';
import type {FacetsV2Service} from '../../../../../utils/services/facets/facets-v2.service';
import type {SearchApiDocument} from '../../../../../api/model/response/documents/SearchResult';

const {info, warn, err} = makeLogger({
  pathToCurrentFile:
    'components/result-page/search-tab/subcomponents/results-block-top/results-block-top.tsx',
});


@Component({
  tag: 'uiaas-search-results-block-top',
  styleUrl: 'results-block-top.scss',
  shadow: true,
})
export class ResultsBlockTop {
  @Prop() readonly queryResult: SearchApi | any; // eslint-disable-line
  @Prop() readonly sortOption: any;  // eslint-disable-line
  @Prop() readonly sortingResults: boolean = false;
  @Prop() readonly showChips: boolean = false;
  @Prop() readonly showChildren: FacetsShowChildrenType = 'allways';
  @Prop() readonly facetsV2Service: FacetsV2Service;
  @Prop() readonly clearHandler: Function;
  @Prop() readonly filtersChipsState: Array<FacetValue|FilterNode> = [];
  @Prop() readonly hiddenFacets: string[] = [];
  @Prop() readonly dividerFacets: string[] = [];
  @Prop() readonly tabName: string;
  @Prop() readonly gridViewSwitch: boolean = false;
  @Prop() readonly switchGridView: Function;
  @Prop() readonly gridView: boolean = false;
  @Prop() readonly aggregatedResults:SearchApiDocument[] = [];
  @Prop() readonly accessibilityMode: any; // eslint-disable-line
  @Prop() readonly rtl: boolean = false;
  @Prop({mutable: true}) labelProvider;

  @Event({
    eventName: 'facest:chipRemoveOnTab',
    composed: true,
    cancelable: true,
    bubbles: true
  }) chipRemoveOnTab: EventEmitter<unknown>;

  @Listen('close:chip')
  handleCloseChip(event: CustomEvent) {
      const eventData = {
        ...{tabName: this.tabName},
        ...event.detail
      };
      this.chipRemoveOnTab.emit(eventData);
  }

  get isSortResults(): boolean {
      return this.queryResult.sortOptions?.length > 0 && this.sortingResults;
  }

  get isBlockTopLeft(): boolean {
      return this.isSortResults || this.gridViewSwitch;
  }

  componentWillLoad() {
      if (!this.labelProvider) {
        info('Assigning default labelProvider to ResultsBlockTop component');

        this.labelProvider = (name, fallback) => {
            warn('LabelProvider not provided in ResultsBlockTop component', [
              name,
              fallback,
            ]);
            return fallback;
        };
      }
  }


  render() {
      const hasQueryResults = this.queryResult && this.queryResult.pagination?.docsCurrent > 0;

      if (!hasQueryResults) {
        return null; // Do not render anything if conditions are not met
      }

      return (
        <Host>
          <div class={'result-block-top'}>
            <div class={'result-block-top__left'}>
              <span class={'result-block-top__title'}>
                {`${this.aggregatedResults?.length} ${this.labelProvider('load-more.of', 'of')} ${this.queryResult?.searchResults?.totalHits} ${this.labelProvider('tab.' + this.queryResult?.searcher, this.queryResult?.searcher?.split('/').pop() )?.toLowerCase()}`}
              </span>
            </div>
            <div class={'result-block-top__right'}>
              {this.isSortResults ? <ResultsSorting
                sortingOptions={this.queryResult.sortOptions}
                defaultValueId={this.sortOption?.id || sortingOptions.RELEVANCY}
                enableSorting={this.sortingResults}
                labelProvider={(e, f) => {
                    return this.labelProvider(e, f);
                }}
              /> : ''}
              {this.gridViewSwitch ?
                <GridSwitchButton
                  gridViewSwitch={this.gridViewSwitch}
                  switchGridView={(e)=>this.switchGridView(e)}
                  gridView={this.gridView}
                  accessibilityMode={this.accessibilityMode}
                  labelProvider={(e, f) => {return this.labelProvider(e, f);}}
                /> : ''}
            </div>
          </div>

          {this.showChips &&
              <div class={'result-block-top-chips'}>
                <svs-chips-view
                  filtersChips={this.filtersChipsState}
                  showChildren={this.showChildren}
                  clearHandler={this.clearHandler}
                  hiddenFacets={this.hiddenFacets}
                  dividerFacets={this.dividerFacets}
                  facetsV2Service={this.facetsV2Service}
                >
                </svs-chips-view>
              </div>}
        </Host>
      );
  }
}
